import { StoreScheduleExclusionResponse } from '@ydistri/api-sdk';
import { Column, Row, Section, Spinner, Text } from '@ydistri/ds';
import { addToast } from '@ydistri/utils';
import { Checkbox, Empty } from 'antd';
import { parseISO } from 'date-fns';
import { formatDate } from '@ydistri/generic-utils';
import { computeRemSize } from '@ydistri/theme';
import React, { useCallback, useMemo, useState } from 'react';
import {
  useDisplayStoreComparator,
  useDisplayStoreLabelFormatter,
} from '../../../hooks/useDisplayStoreLabel';
import useIsConfigurationEditable from '../../../hooks/useIsConfigurationEditable';
import { useTemplateOrCalculationWithValue } from '../../../hooks/useTemplateOrCalculationWithValue';
import { useAppDispatch } from '../../../store';
import {
  useGetStoreScheduleExclusionsQuery,
  useSetStoreScheduleExclusionMutation,
} from './apiStoreScheduleExclusions';
import { compareForDisplay, describeExclusion } from './storeScheduleExclusionsLib';

/** Always present, so the spinner appearing in it does not shift the row. */
const SPINNER_SLOT = computeRemSize(20);

const StoreScheduleExclusionsSection: React.FC = () => {
  const dispatch = useAppDispatch();
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const isConfigurationEditable = useIsConfigurationEditable();
  const formatStoreLabel = useDisplayStoreLabelFormatter();
  const compareStores = useDisplayStoreComparator();

  const {
    data: exclusions,
    isFetching,
    isError,
  } = useGetStoreScheduleExclusionsQuery(templateOrCalculation.value);
  const [setExclusion] = useSetStoreScheduleExclusionMutation();
  // Per record rather than one flag for the section: the checkbox shows the new value at once, and the
  // row's spinner says the write is still in flight.
  const [pending, setPending] = useState<ReadonlySet<number>>(new Set());

  const toggleHandler = useCallback(
    (exclusion: StoreScheduleExclusionResponse) => () => {
      const { storeScheduleId } = exclusion;
      setPending(current => new Set(current).add(storeScheduleId));

      setExclusion({
        templateId: templateOrCalculation.id,
        storeScheduleId,
        isEnabled: !exclusion.isEnabled,
      })
        .unwrap()
        .catch(() => {
          dispatch(addToast({ message: 'The closure was not changed', isError: true }));
        })
        .finally(() => {
          setPending(current => {
            const next = new Set(current);
            next.delete(storeScheduleId);
            return next;
          });
        });
    },
    [setExclusion, templateOrCalculation.id, dispatch],
  );

  const rows = useMemo(
    () => Array.from(exclusions ?? []).sort(compareForDisplay(compareStores)),
    [exclusions, compareStores],
  );
  // Present only while the schedules were evaluated; a calculation reads what it froze.
  const evaluatedOn = rows.find(row => row.evaluatedOn)?.evaluatedOn;

  return (
    <Section header="Stores schedule" $ratio={0} $contentMarginTop={false}>
      {evaluatedOn && (
        <Column $gap="0.25rem">
          <Text $semiBold>Closures in effect on {formatDate(parseISO(evaluatedOn))}</Text>
          <Text $type="light">
            Unticking one puts the store back into this template&apos;s proposal.
          </Text>
        </Column>
      )}
      {isError && (
        // An empty list would otherwise read as "no store is closed".
        <Text $type="danger">The closures could not be loaded.</Text>
      )}
      {!isFetching && !isError && rows.length === 0 && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No closures" />
      )}
      <Column $gap="0.5rem">
        {rows.map(exclusion => (
          <Row
            key={exclusion.storeScheduleId}
            $gap="0.5rem"
            $alignItems="center"
            $flexWrap="nowrap"
          >
            <Row $width={SPINNER_SLOT} $minWidth={SPINNER_SLOT} $alignItems="center">
              {pending.has(exclusion.storeScheduleId) && <Spinner />}
            </Row>
            {/* The row sits inside the checkbox so its whole label toggles the box. */}
            <Checkbox
              checked={exclusion.isEnabled}
              disabled={!isConfigurationEditable || pending.has(exclusion.storeScheduleId)}
              onChange={toggleHandler(exclusion)}
            >
              <Row $gap="0.5rem" $alignItems="center" $flexWrap="nowrap">
                <Text $semiBold>
                  {formatStoreLabel(
                    exclusion.store.name,
                    exclusion.store.customerStoreId,
                    exclusion.store.code,
                  )}
                </Text>
                <Text $type="light">{describeExclusion(exclusion)}</Text>
                {exclusion.excludedUntil && (
                  <Text $type="light">until {formatDate(parseISO(exclusion.excludedUntil))}</Text>
                )}
              </Row>
            </Checkbox>
          </Row>
        ))}
      </Column>
    </Section>
  );
};

export default StoreScheduleExclusionsSection;
